import { useNavigate } from 'react-router-dom';
import { markAppReload } from 'src/lib/perf/reloadClassifier';
import { useMessengerContext } from '../pages/MessengerV2/Messenger.hook';
import { useModalsContext } from '../providers/ModalsProvider/Modals.context.ts';
import { INITIAL_SUBSCRIPTION_VALUES } from '../providers/SubscriptionProvider/Subscription.constants.ts';
import { useSubscriptionContext } from '../providers/SubscriptionProvider/Subscription.context.ts';
import { useWorkspaceContext } from '../providers/WorkspaceProvider/Workspace.context.ts';

/**
 * Single owner of "switch to another workspace".
 *
 * The switch is not just `setCurrentWorkspaceId`: it must also tear down
 * state that belongs to the workspace being left, and — critically — reset
 * the subscription slice. `subscription` is NOT re-derived on a workspace
 * change, so a switcher that skips `setSubscription`/`setIsLoadingSubscription`
 * leaves every plan/ACL gate reading the PREVIOUS workspace's plan until the
 * refetch lands. Any gate consuming it must also check `!isLoadingSubscription`.
 *
 * This lives in a hook rather than in a component because there are now two
 * entry points (the sidebar selector and the navbar title). Duplicating the
 * teardown across both is how the two drift apart.
 */
export function useWorkspaceSwitch() {
  const navigate = useNavigate();
  const { setSelectedLeadForChatModal } = useModalsContext();
  const { setLeads } = useMessengerContext();
  const { setSubscription, setIsLoadingSubscription } =
    useSubscriptionContext();
  const {
    workspaces,
    currentWorkspaceId,
    setCurrentWorkspace,
    setCurrentWorkspaceId,
  } = useWorkspaceContext();

  function switchWorkspace(workspaceId: string) {
    // Switching to the workspace already open is a no-op. Without this the
    // navbar entry point would trigger a full reload (`navigate(0)`) just for
    // re-picking the active item — cheap to guard, expensive to hit.
    if (workspaceId === currentWorkspaceId) return;

    const targetWorkspace = workspaces?.workspaces.find(
      (workspace) => workspace.id === workspaceId,
    );

    // Unknown id: leave the current workspace mounted rather than tearing
    // state down into an unrecoverable blank state.
    if (!targetWorkspace) return;

    setSelectedLeadForChatModal(null);
    setLeads([]);
    setCurrentWorkspace(undefined);
    setCurrentWorkspaceId(undefined);
    setSubscription(INITIAL_SUBSCRIPTION_VALUES);
    setIsLoadingSubscription(true);

    setCurrentWorkspace(targetWorkspace);
    setCurrentWorkspaceId(workspaceId);

    // Full reload. The workspace id seeds provider state that is only read at
    // mount (sockets, caches, ACL), so the app is re-cold-booted rather than
    // re-reconciled. Kept as-is: making the switch reactive is a separate,
    // much larger change than adding a second entry point to it.
    markAppReload();
    navigate(0);
  }

  return { switchWorkspace };
}
